<div class="modal-header">
  <h4 class="modal-title">
    <ng-container *ngIf="inputPricingMatrix !== null">
      Edit
    </ng-container>
    <ng-container *ngIf="inputPricingMatrix === null">
      Add
    </ng-container>
    Location-Specific Pricing Policy
  </h4>
  <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
    <span aria-hidden="true">&times;</span>
  </button>
</div>

<div class="modal-body">

  <div class="form-group">
    <label>Locations</label>
    <div class="form-check ml-3" *ngFor="let location of locations">
      <input class="form-check-input" type="checkbox"
             [id]="'location-' + location.id.toString()"
             [checked]="this.pricingMatrix.location_ids.includes(location.id)"
             (change)="toggleLocationId(location.id)">
      <label class="form-check-label"
             [for]="'location-' + location.id.toString()">
        {{ location.name }}
      </label>
    </div>
  </div>

  <div class="form-group" *ngIf="membership.purchase_type === MembershipPurchaseType.recurring">
    <label for="initiationFeeInput">Initiation Fee *</label>
    <div class="input-group">
      <div class="input-group-prepend">
        <span class="input-group-text">{{ orgConfig.getCurrencySymbol() }}</span>
      </div>
      <input id="initiationFeeInput" type="number" name="initiation_fee"
             placeholder="XXX.XX" min="0" step="0.01"
             class="form-control"
             [(ngModel)]="pricingMatrix.initiation_fee">
    </div>
    <div class="form-text text-danger">{{ errors.initiation_fee }}</div>
  </div>

  <ng-container *ngIf="!membership.is_group">

    <div class="form-group">
      <label for="membershipFeeInput">Membership Fee *</label>
      <div class="input-group">
        <div class="input-group-prepend">
          <span class="input-group-text">{{ orgConfig.getCurrencySymbol() }}</span>
        </div>
        <input id="membershipFeeInput" type="number" name="membership_fee"
               placeholder="XXX.XX" min="0" step="0.01"
               class="form-control"
               [(ngModel)]="pricingMatrix.membership_fee">
      </div>
      <div class="form-text text-danger">{{ errors.membership_fee }}</div>
    </div>

  </ng-container>

  <ng-container *ngIf="membership.is_group">

    <div class="form-group">
      <label for="adultBaseFeeInput">Base Fee per {{ membership.adult_tier_name }} *</label>
      <div class="input-group">
        <div class="input-group-prepend">
          <span class="input-group-text">{{ orgConfig.getCurrencySymbol() }}</span>
        </div>
        <input id="adultBaseFeeInput" type="number" name="adult_base_fee"
               placeholder="XXX.XX" min="0" step="0.01"
               class="form-control"
               [(ngModel)]="pricingMatrix.adult_base_fee">
      </div>
      <div class="form-text text-danger">{{ errors.adult_base_fee }}</div>
    </div>

    <div class="form-group" *ngIf="membership.has_youth_tier">
      <label for="youthBaseFeeInput">Base Fee per {{ membership.youth_tier_name }} *</label>
      <div class="input-group">
        <div class="input-group-prepend">
          <span class="input-group-text">{{ orgConfig.getCurrencySymbol() }}</span>
        </div>
        <input id="youthBaseFeeInput" type="number" name="youth_base_fee"
               placeholder="XXX.XX" min="0" step="0.01"
               class="form-control"
               [(ngModel)]="pricingMatrix.youth_base_fee">
      </div>
      <div class="form-text text-danger">{{ errors.youth_base_fee }}</div>
    </div>

    <div class="form-group">
      <label for="discountTypeSelect">Discounts Represented As *</label>
      <select id="discountTypeSelect" name="discount_type"
              class="custom-select"
              [(ngModel)]="pricingMatrix.discount_type">
        <option [ngValue]="MembershipDiscountType.currency">
          {{ orgConfig.getCurrencyName() }} amounts
        </option>
        <option [ngValue]="MembershipDiscountType.percentage">Percentages</option>
      </select>
      <div class="form-text text-danger">{{ errors.discount_type }}</div>
    </div>

    <div class="form-group">
      <div class="form-check">
        <input id="discountsAreIncrementalCheckbox" type="checkbox" name="discounts_are_incremental"
               class="form-check-input"
               [(ngModel)]="pricingMatrix.discounts_are_incremental">
        <label class="form-check-label" for="discountsAreIncrementalCheckbox">
          Discounts are applied incrementally
        </label>
      </div>
      <small class="form-text text-muted">
        If this is checked, then discounts will be applied only to the member at each number. If this is left unchecked, then discounts are applied to all members when the count is reached. See the Pricing Matrix Preview to ensure that discounts are being calculated in the desired fashion.
      </small>
      <div class="form-text text-danger">{{ errors.discounts_are_incremental }}</div>
    </div>

    <div class="form-group"
         *ngIf="(membership.purchase_type === MembershipPurchaseType.recurring) && !pricingMatrix.discounts_are_incremental">
      <div class="form-check">
        <input id="discounts_apply_to_individually_frozen_members"
               name="discounts_apply_to_individually_frozen_members"
               type="checkbox"
               class="form-check-input"
               [(ngModel)]="pricingMatrix.discounts_apply_to_individually_frozen_members">
        <label class="form-check-label" for="discounts_apply_to_individually_frozen_members">
          Individually frozen members counts towards discount tiers
        </label>
      </div>
      <small class="form-text text-muted">
        If this is checked, then individually frozen members will still count towards the discount tier (potentially resulting in a lower price than if the frozen members were ignored). If this is left unchecked, then the membership fee calculation will ignore any individually frozen members.
      </small>
      <div class="form-text text-danger">{{ errors.discounts_apply_to_individually_frozen_members }}</div>
    </div>

    <hr class="my-4">

    <div class="rounded-table-wrapper mb-1">
      <table class="table">

        <thead>
        <tr>
          <th># in {{ membership.adult_tier_name }} Tier</th>
          <th>Discount per {{ membership.adult_tier_name }}</th>
          <th>Price per {{ membership.adult_tier_name }}</th>
          <th></th>
        </tr>
        </thead>

        <tbody>
        <tr *ngFor="let adultDiscount of pricingMatrix.adult_discounts">
          <td>{{ adultDiscount.number_in_tier }}</td>
          <td class="table-button-cell">
            <div class="input-group input-group-sm" style="max-width: 150px">
              <div class="input-group-prepend"
                   *ngIf="pricingMatrix.discount_type === MembershipDiscountType.currency">
                <span class="input-group-text">{{ orgConfig.getCurrencySymbol() }}</span>
              </div>
              <input class="form-control form-control-sm"
                     type="number" step="0.01" min="0"
                     [(ngModel)]="adultDiscount.discount_amount"
                     [ngModelOptions]="{standalone: true}">
              <div class="input-group-append"
                   *ngIf="pricingMatrix.discount_type === MembershipDiscountType.percentage">
                <span class="input-group-text">%</span>
              </div>
            </div>
          </td>
          <td>{{ orgConfig.getCurrencySymbol() }}{{ getDiscountedPrice(pricingMatrix.adult_base_fee, adultDiscount.discount_amount) | number:'1.2-2' }}</td>
          <td class="table-button-cell">
            <button type="button" class="btn btn-outline-danger btn-sm float-right"
                    *ngIf="(adultDiscount.number_in_tier === pricingMatrix.adult_discounts.length) && pricingMatrix.adult_discounts.length > 1"
                    (click)="pricingMatrix.adult_discounts.pop()">
              Remove
            </button>
          </td>
        </tr>
        </tbody>

      </table>
    </div>

    <button type="button" class="btn btn-outline-primary mb-3"
            (click)="addAdultDiscount()">
      Add Option
    </button>

    <p *ngIf="errors.adult_discounts" class="text-danger">{{ errors.adult_discounts }}</p>

    <hr class="my-4">

    <ng-container *ngIf="membership.has_youth_tier">

      <div class="rounded-table-wrapper mb-1">
        <table class="table">

          <thead>
          <tr>
            <th># in {{ membership.youth_tier_name }} Tier</th>
            <th>Discount per {{ membership.youth_tier_name }}</th>
            <th>Price per {{ membership.youth_tier_name }}</th>
            <th></th>
          </tr>
          </thead>

          <tbody>
          <tr *ngFor="let youthDiscount of pricingMatrix.youth_discounts">
            <td>{{ youthDiscount.number_in_tier }}</td>
            <td class="table-button-cell">
              <div class="input-group input-group-sm" style="max-width: 150px">
                <div class="input-group-prepend"
                     *ngIf="pricingMatrix.discount_type === MembershipDiscountType.currency">
                  <span class="input-group-text">{{ orgConfig.getCurrencySymbol() }}</span>
                </div>
                <input class="form-control form-control-sm"
                       type="number" step="0.01" min="0"
                       [(ngModel)]="youthDiscount.discount_amount"
                       [ngModelOptions]="{standalone: true}">
                <div class="input-group-append"
                     *ngIf="pricingMatrix.discount_type === MembershipDiscountType.percentage">
                  <span class="input-group-text">%</span>
                </div>
              </div>
            </td>
            <td>{{ orgConfig.getCurrencySymbol() }}{{ getDiscountedPrice(pricingMatrix.youth_base_fee, youthDiscount.discount_amount) | number:'1.2-2' }}</td>
            <td class="table-button-cell">
              <button type="button" class="btn btn-outline-danger btn-sm float-right"
                      *ngIf="(youthDiscount.number_in_tier === pricingMatrix.youth_discounts.length) && pricingMatrix.youth_discounts.length > 1"
                      (click)="pricingMatrix.youth_discounts.pop();">
                Remove
              </button>
            </td>
          </tr>
          </tbody>

        </table>
      </div>

      <button type="button" class="btn btn-outline-primary mb-3"
              (click)="addYouthDiscount();">
        Add Option
      </button>

      <p *ngIf="errors.youth_discounts" class="text-danger">{{ errors.youth_discounts }}</p>

      <hr class="my-4">

    </ng-container><!-- Youth tier discounts -->

    <h5 class="mt-3">Pricing Matrix Preview</h5>

    <ng-container *ngIf="!membership.has_youth_tier">
      <div class="rounded-table-wrapper" style="max-width: 200px">
        <table class="table">
          <tbody>
          <tr *ngFor="let adultNum of getPricingMatrixAdultNums()">
            <th>
              {{ adultNum }} {{ membership.adult_tier_name }}{{ adultNum === 1 ? '' : 's' }}
            </th>
            <td>
              <ng-container *ngIf="memberNumAllowed(adultNum, 0)">
                {{ orgConfig.getCurrencySymbol() }}{{ getGroupPrice(adultNum, 0) }}
              </ng-container>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </ng-container><!-- Pricing matrix - no youth tier -->

    <ng-container *ngIf="membership.has_youth_tier">
      <div class="rounded-table-wrapper">
        <table class="table">
          <thead>
          <tr>
            <th></th>
            <th *ngFor="let adultNum of getPricingMatrixAdultNums()">
              {{ adultNum }} {{ membership.adult_tier_name }}{{ adultNum === 1 ? '' : 's' }}
            </th>
          </tr>
          </thead>

          <tbody>
          <tr *ngFor="let youthNum of getPricingMatrixYouthNums()">
            <th>
              {{ youthNum }} {{ membership.youth_tier_name }}{{ youthNum === 1 ? '' : 's' }}
            </th>
            <td *ngFor="let adultNum of getPricingMatrixAdultNums()">
              <ng-container *ngIf="memberNumAllowed(adultNum, youthNum)">
                {{ orgConfig.getCurrencySymbol() }}{{ getGroupPrice(adultNum, youthNum) }}
              </ng-container>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </ng-container><!-- Pricing matrix - with youth tier -->

    <hr class="my-4">

  </ng-container><!-- Group membership pricing -->

  <div class="form-group mt-4">
    <b class="mb-2 d-block">Add-On Pricing</b>

    <div class="mb-3"
         *ngFor="let addOn of membership.per_customer_add_ons">
      <label for="addOnPrice{{ addOn.id }}">
        {{ addOn.name }}
      </label>
      <div class="input-group">
        <div class="input-group-prepend">
          <span class="input-group-text">{{ orgConfig.getCurrencySymbol() }}</span>
        </div>
        <input id="addOnPrice{{ addOn.id }}"
               name="addOnPrice{{ addOn.id }}"
               type="number"
               placeholder="XXX.XX" min="0" step="0.01"
               class="form-control"
               [(ngModel)]="pricingMatrix.per_customer_add_on_fees[addOn.id]">
      </div>
    </div>

    <span *ngIf="membership.per_customer_add_ons.length === 0"
          class="text-muted">
      No add-ons configured.
    </span>

    <div class="form-text text-danger">{{ errors.per_customer_add_on_fees }}</div>
  </div>

  <div class="alert alert-info">
    Add-on names and icons must be the same across all locations.
    <br>
    Leave an add-on's price field blank in order to make the add-on unavailable at the selected locations.
  </div>

  <div class="alert alert-danger" role="alert" *ngIf="errorMessage">
    {{ errorMessage }}
  </div>

</div>

<div class="modal-footer">
  <button type="button" class="btn btn-secondary"
          (click)="activeModal.close()">
    Cancel
  </button>
  <button class="btn btn-primary"
          (click)="handleSubmit()">
    Submit
  </button>
</div>
